import { Box, Group, Stack, Text } from "@mantine/core"

import classes from "./StepperRail.module.css"

export type StepperRailStep = Readonly<{
  id: string
  label: string
  stepNumber?: number
}>

export type StepperRailProps = Readonly<{
  steps: readonly StepperRailStep[]
  currentStepIndex: number
  /** Accessible name for the stepper landmark (e.g. translated flow title). */
  ariaLabel: string
}>

export const StepperRail = ({
  steps,
  currentStepIndex,
  ariaLabel,
}: StepperRailProps) => {
  return (
    <Group
      component="section"
      aria-label={ariaLabel}
      px="var(--mantine-spacing-xs)"
      justify="space-between"
      align="flex-start"
      gap={0}
      wrap="nowrap"
      style={{ flexShrink: 0 }}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentStepIndex
        const isActive = index === currentStepIndex
        const isLastStep = index === steps.length - 1
        const stepNumber = step.stepNumber ?? index + 1

        const stepCircleState = isCompleted
          ? "completed"
          : isActive
            ? "active"
            : "upcoming"

        return (
          <Stack
            key={step.id}
            align="center"
            gap="var(--mantine-spacing-xs)"
            flex={1}
            pos="relative"
            style={{ zIndex: 1 }}
            aria-current={isActive ? "step" : undefined}
          >
            <Box
              aria-hidden
              className={classes.stepCircle}
              data-state={stepCircleState}
            >
              {isCompleted ? "✓" : stepNumber}
            </Box>
            <Text
              component="div"
              size="xs"
              fw={isActive ? 700 : 500}
              c={
                isActive || isCompleted
                  ? "var(--mantine-color-text-color)"
                  : "var(--tfg-text-color-secondary)"
              }
              ta="center"
              maw={110}
              lh={1.3}
            >
              {step.label}
            </Text>
            {!isLastStep ? (
              <Box
                aria-hidden
                className={classes.connectorLine}
                data-completed={isCompleted || undefined}
              />
            ) : null}
          </Stack>
        )
      })}
    </Group>
  )
}
